import { AttributeType, GeometryType, StringSubType } from "@evergis/api";
import { Icon, IconToggle, IJSXOption, OptionIcon } from "@evergis/ui";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DraggableProvided } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { fieldTypes } from "../../../evergis/core/attributes/fieldTypes";
import { useAttributesError } from "../../../hooks/useAttributesError";
import { AttributeError, validateAttributeName } from "../../../utils/validateAttributeName";
import { AttributeFormat } from "../DialogComponents/Attributes/AttributeFormat/AttributeFormat";
import { AttributeActions } from "../EditResource/components/QueryLayerResource/AttributeActions";
import {
  AliasInput,
  NameInput,
  QueryAttributeNewContainer,
  StyledDropdown,
} from "../EditResource/components/QueryLayerResource/styled";
import { EqlAttribute } from "../EditResource/components/QueryLayerResource/types";

export interface TableAttributeNewProps {
  attributeNames: string[];
  attribute: EqlAttribute;
  draggableProvided?: DraggableProvided;
  updateAttribute: (name: string, attributeUpdate: Partial<EqlAttribute>) => void;
  deleteAttribute: (name: string) => void;
  deleteSysAttribute: (name: string) => void;
  setDisplayField?: (displayField: string) => void;
  displayField?: string;
  geometryType: GeometryType;
}

const typesOptions = fieldTypes.filter(
  value => ![AttributeType.Point, AttributeType.MultiPolygon].includes(value.value as AttributeType),
);

// eslint-disable-next-line max-lines-per-function
export const NewLayerAttribute: FC<TableAttributeNewProps> = ({
  attributeNames,
  attribute,
  draggableProvided,
  updateAttribute,
  deleteAttribute,
  deleteSysAttribute,
  displayField,
  setDisplayField,
  geometryType,
}) => {
  const { t, i18n } = useTranslation("dialogs");

  const { id, attributeName, type, subType, alias, sysAttribute, stringFormat, expression, description } = attribute;

  const [error, setError] = useState<AttributeError>();

  const { help, error: attributesError } = useAttributesError(error);

  const isNameChanged = useRef(false);
  const element = useRef<HTMLElement>();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const attributeNamesSet = useMemo(() => new Set<string>(attributeNames), [attributeNames.length]);

  const updateName = useCallback(
    (newName: string) => {
      updateAttribute(id, {
        attributeName: newName,
        columnName: newName,
      });
      setError(validateAttributeName(newName, attributeNamesSet));
      isNameChanged.current = true;
    },
    [id, updateAttribute, attributeNamesSet],
  );

  const updateType = useCallback(
    (newType: AttributeType | StringSubType) => {
      let typeFormatted;

      if (newType === StringSubType.Image) {
        typeFormatted = {
          type: AttributeType.String,
          subType: StringSubType.Image,
        };
      } else if (newType === StringSubType.Attachments) {
        typeFormatted = {
          type: AttributeType.Json,
          subType: StringSubType.Attachments,
        };
      } else {
        typeFormatted = {
          type: newType,
        };
      }

      updateAttribute(id, typeFormatted);
    },
    [id, updateAttribute],
  );

  const updateAlias = useCallback(
    (text: string) => {
      updateAttribute(id, {
        alias: text,
      });
    },
    [id, updateAttribute],
  );

  const canBeDisplayField =
    type === AttributeType.String && (!subType || subType === StringSubType.None) && !sysAttribute;

  const renderResultOptions = useCallback(selectedOptions => {
    if (selectedOptions && selectedOptions.length > 0) {
      return selectedOptions.map((item: IJSXOption) => {
        return (
          <OptionIcon key={item.value}>
            <Icon kind={item.icon} />
          </OptionIcon>
        );
      });
    }

    return null;
  }, []);

  useEffect(() => {
    element.current?.scrollIntoView();
    element.current?.focus();
  }, []);

  return (
    <QueryAttributeNewContainer
      {...draggableProvided.draggableProps}
      {...draggableProvided.dragHandleProps}
      ref={ref => {
        draggableProvided.innerRef(ref);
        element.current = ref;
      }}
    >
      <IconToggle style={{ marginRight: "1rem" }} isSelected={false} kind={"password_show"} disabled />
      <StyledDropdown
        disabled={sysAttribute}
        value={
          type === AttributeType.Json
            ? StringSubType.Attachments
            : subType === StringSubType.Image
            ? StringSubType.Image
            : type
        }
        width={"3.5rem"}
        options={typesOptions.map(value => ({
          ...value,
          text: t(value.langKey, { ns: "common" }),
        }))}
        menuMinWidth={i18n.language === "en" ? "10rem" : "12rem"}
        renderResultOptions={renderResultOptions}
        onChange={val => updateType(val[0].value as AttributeType)}
        zIndex={120}
      />
      <NameInput
        width={"100%"}
        value={attributeName}
        onChange={event => updateName(event.target.value)}
        placeholder={t("newLayer.attributeNameLat", { ns: "dialogs" })}
        label={t("newLayer.attributeName", { ns: "dialogs" })}
        help={isNameChanged.current && error ? attributesError : help}
        error={isNameChanged.current && !!error}
      />
      <AliasInput
        width={"100%"}
        value={alias}
        onChange={event => updateAlias(event.target.value)}
        placeholder={t("alias", { ns: "style" })}
      />
      <AttributeFormat
        type={type}
        subType={subType}
        geometryType={geometryType}
        id={id}
        updateAttribute={updateAttribute}
        stringFormat={stringFormat}
        isCalculated={!!expression}
        description={description}
        attributeConfiguration={attribute}
        withoutInput
      />
      <AttributeActions
        canBeDisplayField={canBeDisplayField}
        isDisplayField={displayField === attributeName}
        toggleDisplayField={() => setDisplayField(displayField === attributeName ? void 0 : attributeName)}
        deleteAttribute={() => (sysAttribute ? deleteSysAttribute(id) : deleteAttribute(id))}
        canDelete
      />
    </QueryAttributeNewContainer>
  );
};
